import React, { useCallback, useState, useRef } from "react";
import moment from "moment";
import "moment/locale/pt-br";
import "moment/locale/en-nz";
import "moment/locale/es";
import * as Yup from "yup";
import { Formik } from "formik";
/** Redux libs */
import { useSelector, useDispatch } from "react-redux";
import { Creators as LotActions } from "../../../store/ducks/lot";
/** Components */
import {
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  notification,
  Spin,
  Radio,
} from "antd";
import ButtonStandard from "../../utils/button";
import { Container, Footer, CustomDivider, InfoCard } from "./styles";
import NoteIcon from "../../utils/icons/note";
import NumberFormat from "react-number-format";
import InfoTooltip from "../../utils/infoTooltip";
import RadioButton from "../../utils/radioButton";
/** Services */
import { getListOfProfitCenterIndex } from "../../../services/profitCenterService";
import { getDietStrategyIndex } from "../../../services/dietStrategyService";
import {
  getLoatAcquisitionData,
  getLotShow,
  getLotStatistics,
  saveNewLotProduction,
} from "../../../services/lotService";
import { getBreedIndexActive } from "../../../services/breedService";
import { getPicketIndexDropDown } from "../../../services/picketService";
import { getWeightGainProjectionDrowpdown } from "../../../services/weightGainProjectionService";

const INITIAL_FORM_VALUES = {
  id: null,
  name: "",
  breedId: null,
  gender: "Male",
  cap: false,
  identification: null,
  asymptoticWeight: "",
  carcassHarnessing: "",
  supplierId: null,
  profitCenterId: null,
  projectionWeightType: "DIET_STRATEGY",
  startWeightGainProjectionDate: null,
  weightGainProjectionId: null,
  dietStrategyBaseLine: null,
  dietStrategies: [],
  referenceAcquisitionDate: null,
  referenceAcquisitionWeight: "",
  referenceBirthdayDate: null,
  status: "Pending",
  averageAnimalStay: null,
  changeAnimalsFunction: false,
  animalFarmFunction: null,
  animalReproductionCategory: null,
  picketId: null,
  picketLotEntry: null,
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required(),
  profitCenterId: Yup.string().required(),
  changeAnimalsFunction: Yup.boolean(),
  animalFarmFunction: Yup.string()
    .notRequired()
    .when(["changeAnimalsFunction"], {
      is: (changeAnimalsFunction) => changeAnimalsFunction === true,
      then: Yup.string().required(),
      otherwise: Yup.string().notRequired().nullable(),
    }),
  startWeightGainProjectionDate: Yup.date()
    .nullable()
    .when(["projectionWeightType"], {
      is: (projectionWeightType) =>
        projectionWeightType === "FARM_PROJECTION_GAIN_WEIGHT",
      then: Yup.date().required().nullable(),
      otherwise: Yup.date().notRequired().nullable(),
    }),
});

const DrawerLotProduction = () => {
  const dispatch = useDispatch();

  const { translation, groupSelected, farmSelected } = useSelector(
    (s) => s.app,
  );
  const {
    lotIdEdit,
    drawerLotProduction,
    isLoadingRequest,
    paginationLotProduction,
    paginationLotProductionGrowth,
  } = useSelector((s) => s.lot);

  const [breeds, setBreeds] = useState([]);
  const [pickets, setPickets] = useState([]);
  const [formLot, setFormLot] = useState(INITIAL_FORM_VALUES);
  const [listOfProfitCenters, setListOfProfitCenters] = useState(null);
  const [isLoadingDietStrategies, setIsLoadingDietStrategies] = useState(false);
  const [listofDietStrategies, setListofDietStrategies] = useState([]);
  const [weightGainProjections, setWeightGainProjections] = useState([]);
  const [isLoadingWeightGainProjections, setIsLoadingWeightGainProjections] =
    useState(false);
  const [saveOptions, setSaveOptions] = useState("only_save");
  const [isLoading, setIsLoading] = useState(false);
  const [amountAnimals, setAmountAnimals] = useState(null);
  const [showCardUpdateAcquisitionData, setShowCardUpdateAcquisitionData] =
    useState(false);
  const [lotAlreadyHavePicket, setLotAlreadyHavePicket] = useState(false);
  const [selectedPicket, setSelectedPicket] = useState(null);

  const inputNameRef = useRef(null);
  const formikRef = useRef(null);

  const closeDrawer = () => {
    setFormLot(INITIAL_FORM_VALUES);
    formikRef.current.resetForm({
      ...INITIAL_FORM_VALUES,
    });
    dispatch(LotActions.hideDrawerLotProduction());
  };

  const getBreeds = useCallback(async (groupId, farmId) => {
    const {
      data: { results: breedsData },
    } = await getBreedIndexActive({
      groupId,
      farmId,
      withoutBreedCurveParam: true,
    });

    setBreeds(breedsData);
  }, []);

  const getPickets = useCallback(async (groupId, farmId) => {
    const {
      data: { results: picketsData },
    } = await getPicketIndexDropDown({
      groupId,
      farmId,
    });

    setPickets(picketsData);
  }, []);

  const getProfitCenters = useCallback(async (groupSel, farmSel) => {
    const { id: groupId } = groupSel;
    const { id: farmId } = farmSel;

    const {
      data: { results: allProfitCenters },
    } = await getListOfProfitCenterIndex({ groupId, farmId });
    setListOfProfitCenters(
      allProfitCenters.filter((e) => e.status === "Active"),
    );
  }, []);

  const getWeightGainProjections = useCallback(async () => {
    setIsLoadingWeightGainProjections(true);
    try {
      const {
        data: { results },
      } = await getWeightGainProjectionDrowpdown({
        groupId: groupSelected.id,
        farmId: farmSelected.id,
      });
      setWeightGainProjections(results || []);
    } catch {
      setWeightGainProjections([]);
    } finally {
      setIsLoadingWeightGainProjections(false);
    }
  }, [groupSelected.id, farmSelected.id]);

  const getAmountAnimals = useCallback(
    async (idLot) => {
      const {
        data: { results: amountAnimalsData },
      } = await getLotStatistics({
        groupId: groupSelected.id,
        farmId: farmSelected.id,
        id: idLot,
        statistics: "AmountActiveAnimals",
      });
      setAmountAnimals(amountAnimalsData);
    },
    [groupSelected.id, farmSelected.id],
  );

  const getDietStrategies = useCallback(
    async (lot) => {
      try {
        setIsLoadingDietStrategies(true);
        const {
          data: { results: allDietStrategiesData },
        } = await getDietStrategyIndex({
          groupId: groupSelected.id,
          farmId: farmSelected.id,
          withoutPagination: true,
        });
        const allDietStrategiesFiltered = allDietStrategiesData.filter(
          (e) => e.status === "Active",
        );
        setListofDietStrategies(allDietStrategiesFiltered);
        setIsLoadingDietStrategies(false);
      } catch (error) {
        setIsLoadingDietStrategies(false);
      }
    },
    [groupSelected.id, farmSelected.id],
  );

  const getLotStatus = (
    amountAnimals,
    picketId,
    projectionWeightType,
    weightGainProjectionId,
    dietStrategies,
  ) => {
    if (
      projectionWeightType === "FARM_PROJECTION_GAIN_WEIGHT" &&
      weightGainProjectionId != null &&
      amountAnimals > 0
    ) {
      return "Active";
    }
    if (picketId === null || dietStrategies.length === 0) {
      return "Pending";
    } else if (
      picketId !== null &&
      dietStrategies.length > 0 &&
      amountAnimals > 0
    ) {
      return "Active";
    } else {
      return "Pending";
    }
  };

  const textModal = () => {
    return (
      <div>
        <p>
          Conclua os passos abaixo para{" "}
          <span style={{ fontWeight: "bold" }}>tornar o lote ativo</span>
        </p>
        <ul style={{ marginLeft: 17 }}>
          <li>
            <span style={{ fontWeight: "bold" }}>Adicione animais</span> no lote
          </li>
          <li>
            Aloque o lote em um{" "}
            <span style={{ fontWeight: "bold" }}>piquete/baia</span>
          </li>
          <li>
            Defina uma{" "}
            <span style={{ fontWeight: "bold" }}>Estratégia de dieta</span> para
            o lote em <span style={{ fontWeight: "bold" }}>Manejos</span>
          </li>
        </ul>
      </div>
    );
  };

  const handleSubmitModalForm = async (values, actions) => {
    const dietStrategies =
      values.dietStrategies != null && values.dietStrategies.length > 0
        ? values.dietStrategies.map((ds) => ({
            dietStrategyId: ds.dietStrategyId,
            baseline: ds.baseline,
          }))
        : [];
    let lot = {
      id: values.id,
      name: values.name,
      identification: values.identification,
      asymptoticWeight: values.asymptoticWeight,
      carcassHarnessing: values.carcassHarnessing,
      profitCenterId: values.profitCenterId,
      dietStrategies: [...dietStrategies],
      breedId: values.breedId,
      gender: values.gender,
      cap: values.cap,
      referenceAcquisitionDate: values.referenceAcquisitionDate,
      referenceAcquisitionWeight: values.referenceAcquisitionWeight,
      referenceBirthdayDate: values.referenceBirthdayDate,
      status: getLotStatus(
        amountAnimals,
        values.picketId,
        values.projectionWeightType,
        values.weightGainProjectionId,
        dietStrategies,
      ),
      averageAnimalStay: values.averageAnimalStay,
      changeAnimalsFunction: values.changeAnimalsFunction,
      animalFarmFunction: values.animalFarmFunction,
      animalReproductionCategory: values.animalReproductionCategory,
      picketId: values.picketId || null,
      picketLotEntry: values.picketLotEntry || null,
      projectionWeightType: values.projectionWeightType || null,
      startWeightGainProjectionDate:
        values.projectionWeightType === "FARM_PROJECTION_GAIN_WEIGHT"
          ? moment(values.startWeightGainProjectionDate).format("YYYY-MM-DD") ||
            null
          : null,
      weightGainProjectionId: values.weightGainProjectionId || null,
    };
    if (
      lotAlreadyHavePicket === false &&
      lot.picketId !== null &&
      lot.picketLotEntry == null
    ) {
      notification.error({
        message:
          "É necessário informar uma data de entrada do lote no piquete selecionado.",
      });
      formikRef.current.setErrors({
        picketLotEntry:
          "É necessário informar uma data de entrada do lote no piquete",
      });
      return;
    }
    if (
      lot.carcassHarnessing != null &&
      lot.carcassHarnessing !== "" &&
      (lot.carcassHarnessing < 45 || lot.carcassHarnessing > 60)
    ) {
      notification.error({
        message:
          'O valor do campo "Aproveitamento de Carcaça" deve estar entre o intervalo de valores (45% e 60%)',
      });
      return;
    } else {
      if (values.id === null) {
        handleNewLot(lot);
      } else {
        handleUpdateLot(lot, values.id);
      }
    }
  };

  const handleNewLot = async (lot) => {
    const { page, sorter, filters, size, tableProdSorters, tableProdFilters } =
      paginationLotProduction;
    const {
      page: pageGrowth,
      sorter: sorterGrowth,
      filters: filtersGrowth,
      size: sizeGrowth,
      tableProdSorters: tableProdSortersGrowth,
      tableProdFilters: tableProdFiltersGrowth,
    } = paginationLotProductionGrowth;

    const isSaveAndNew = saveOptions !== "only_save";
    setIsLoading(true);
    try {
      await saveNewLotProduction({
        groupId: groupSelected.id,
        farmId: farmSelected.id,
        lotProduction: lot,
      });
      notification.success({
        message: "Cadastro realizado com sucesso",
        description: "Lote de produção criado com sucesso",
      });
      dispatch(
        LotActions.indexLotsProduction(
          groupSelected.id,
          farmSelected.id,
          page,
          sorter,
          filters,
          size,
          null,
          tableProdSorters,
          tableProdFilters,
        ),
      );
      dispatch(
        LotActions.indexLotsProductionGrowth(
          groupSelected.id,
          farmSelected.id,
          pageGrowth,
          sorterGrowth,
          filtersGrowth,
          sizeGrowth,
          null,
          tableProdSortersGrowth,
          tableProdFiltersGrowth,
        ),
      );
      dispatch(
        LotActions.getLotsProductionDropDown(groupSelected.id, farmSelected.id),
      );
      setIsLoading(false);
      if (lot?.status === "Pending") {
        notification.warn({
          message: translation.lot.production.drawer.warningPendingTitle,
          description: textModal(),
          duration: 10,
        });
      }
      if (!isSaveAndNew) {
        closeDrawer();
      } else {
        setFormLot(INITIAL_FORM_VALUES);
        formikRef.current.resetForm({
          ...INITIAL_FORM_VALUES,
        });
      }
    } catch (error) {
      setIsLoading(false);
      if (Object.keys(error).includes("response")) {
        const { response } = error;
        if (response && Object.keys(response).includes("data")) {
          const {
            data: { code: errorCode },
          } = response;
          if (errorCode === 5034) {
            notification.error({
              message: "Cadastro não realizado",
              description:
                "Já existe um lote de produção ativo com o mesmo código de importação.",
            });
          } else {
            notification.error({
              message: "Cadastro não realizado",
              description: "Lote de produção não foi criado",
            });
          }
        }
      } else {
        notification.error({
          message: "Cadastro não realizado",
          description: "Lote de produção não foi criado",
        });
      }
    }
  };

  const handleUpdateLot = async (lot, id) => {
    const { page, sorter, filters, size, tableProdSorters, tableProdFilters } =
      paginationLotProduction;
    const {
      page: pageGrowth,
      sorter: sorterGrowth,
      filters: filtersGrowth,
      size: sizeGrowth,
      tableProdSorters: tableProdSortersGrowth,
      tableProdFilters: tableProdFiltersGrowth,
    } = paginationLotProductionGrowth;

    setIsLoading(true);
    try {
      await saveNewLotProduction({
        groupId: groupSelected.id,
        farmId: farmSelected.id,
        lotProduction: lot,
        id,
      });
      notification.success({
        message: "Lote atualizado",
        description: "Lote de produção atualizado com sucesso",
      });
      dispatch(
        LotActions.indexLotsProduction(
          groupSelected.id,
          farmSelected.id,
          page,
          sorter,
          filters,
          size,
          null,
          tableProdSorters,
          tableProdFilters,
        ),
      );
      dispatch(
        LotActions.indexLotsProductionGrowth(
          groupSelected.id,
          farmSelected.id,
          pageGrowth,
          sorterGrowth,
          filtersGrowth,
          sizeGrowth,
          null,
          tableProdSortersGrowth,
          tableProdFiltersGrowth,
        ),
      );
      dispatch(
        LotActions.getLotsProductionDropDown(groupSelected.id, farmSelected.id),
      );
      setIsLoading(false);
      if (lot?.status === "Pending") {
        notification.warn({
          message: translation.lot.production.drawer.warningPendingTitle,
          description: textModal(),
          duration: 0,
        });
      }
      closeDrawer();
    } catch (error) {
      setIsLoading(false);
      if (Object.keys(error).includes("response")) {
        const { response } = error;
        if (Object.keys(response).includes("data")) {
          const {
            data: { code: errorCode },
          } = response;
          if (errorCode === 5034) {
            notification.error({
              message: "Atualização não realizado",
              description:
                "Já existe um lote de produção ativo com o mesmo código de importação",
            });
          } else {
            notification.error({
              message: "Atualização não realizado",
              description: "Lote de produção não foi atualizado",
            });
          }
        }
      } else {
        notification.error({
          message: "Atualização não realizada",
          description: "Lote de produção não foi atualizado",
        });
      }
    }
  };

  const handleDrawerVisible = useCallback(
    async (visible) => {
      if (visible) {
        setIsLoading(true);
        if (
          Object.entries(groupSelected).length > 0 &&
          Object.entries(farmSelected).length > 0
        ) {
          await Promise.all([
            getProfitCenters(groupSelected, farmSelected),
            getBreeds(groupSelected.id, farmSelected.id),
            getPickets(groupSelected.id, farmSelected.id),
            getWeightGainProjections(),
          ]);
          if (lotIdEdit != null) {
            const {
              data: { results: lotData },
            } = await getLotShow({
              groupId: groupSelected.id,
              farmId: farmSelected.id,
              id: lotIdEdit,
            });
            await Promise.all([
              getDietStrategies(lotData),
              getAmountAnimals(lotIdEdit),
            ]);
            const lotDataForm = {
              ...lotData,
              dietStrategyBaseLine: lotData?.dietStrategies.filter(
                (item) => item.baseline === true,
              )[0],
              dietStrategies: lotData?.dietStrategies.filter(
                (item) => item.baseline !== false,
              ),
            };
            setFormLot(lotDataForm);
            formikRef.current.resetForm(lotDataForm);
            setLotAlreadyHavePicket(lotData?.picketId !== null);
            setIsLoading(false);
            setSelectedPicket(null);
          } else {
            getDietStrategies(null);
            formikRef.current.resetForm(INITIAL_FORM_VALUES);
            setFormLot(INITIAL_FORM_VALUES);
            setIsLoading(false);
            setLotAlreadyHavePicket(false);
          }
        }
      } else {
        setFormLot(INITIAL_FORM_VALUES);
        setSelectedPicket(null);
      }
    },
    [
      formikRef,
      farmSelected,
      getWeightGainProjections,
      getAmountAnimals,
      getBreeds,
      getDietStrategies,
      getPickets,
      getProfitCenters,
      groupSelected,
      lotIdEdit,
    ],
  );

  const handleSelectDietStrategyBaseLine = (value, props) => {
    if (value == null) {
      props.setFieldValue("dietStrategyBaseLine", null);
      props.setFieldValue("dietStrategies", null);
    } else {
      props.setFieldValue("dietStrategyBaseLine", {
        dietStrategyId: value,
        baseline: true,
      });
      props.setFieldValue("dietStrategies", [
        {
          dietStrategyId: value,
          baseline: true,
        },
      ]);
    }
  };

  const handleChangeAge = (e, type, props) => {
    let newDate = null;
    switch (type) {
      case "days":
        newDate = moment().subtract(e.target.value, "days");
        break;
      case "months":
        newDate = moment().subtract(e.target.value, "months");
        break;
      case "years":
        newDate = moment().subtract(e.target.value, "years");
        break;
      default:
        break;
    }
    props.setValues({
      ...props.values,
      averageAnimalStay: moment().diff(newDate, "days"),
    });
  };

  const handleUpdateLotAcquisitionData = async () => {
    if (lotIdEdit !== null) {
      setIsLoading(true);
      try {
        const {
          data: { results },
        } = await getLoatAcquisitionData({
          groupId: groupSelected.id,
          farmId: farmSelected.id,
          lotId: lotIdEdit,
        });
        formikRef.current.setFieldValue("breedId", results.breedId);
        formikRef.current.setFieldValue(
          "gender",
          results.animalGender === null ? "Male" : results.animalGender,
        );
        formikRef.current.setFieldValue(
          "referenceAcquisitionDate",
          results.referenceAcquisitionDate,
        );
        formikRef.current.setFieldValue(
          "referenceAcquisitionWeight",
          results.referenceAcquisitionWeight || "",
        );
        formikRef.current.setFieldValue(
          "referenceBirthdayDate",
          results.referenceBirthdayDate,
        );
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
  };

  const profitCenters = listOfProfitCenters;
  const dietStrategies = listofDietStrategies;

  const { Option } = Select;

  const { Group: RadioGroup } = Radio;

  const { Group: InputGroup } = Input;

  const dateFormat = "DD/MM/YYYY";

  return (
    <Container
      title={
        lotIdEdit != null
          ? translation.lot.production.drawer.titleEdit
          : translation.lot.production.drawer.title
      }
      width={770}
      onClose={closeDrawer}
      maskClosable={false}
      visible={drawerLotProduction}
      afterVisibleChange={handleDrawerVisible}
      zIndex={1005}
    >
      <Formik
        ref={formikRef}
        enableReinitialize={true}
        initialValues={formLot}
        onSubmit={handleSubmitModalForm}
        validationSchema={validationSchema}
        render={(props) => (
          <form onSubmit={props.handleSubmit} autoComplete="off">
            <div className="drawerForm">
              <Spin spinning={isLoading || isLoadingRequest}>
                <Row type="flex">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <span style={{ fontSize: "14px", color: "#000" }}>
                      {lotIdEdit != null
                        ? translation.lot.production.drawer.subtitleEdit
                        : translation.lot.production.drawer.subtitle}
                    </span>
                  </Col>
                </Row>
                {Object.entries(props.errors).length > 0 &&
                  props.submitCount > 0 && (
                    <Row type="flex" justify="center" align="middle">
                      <label className="error">
                        {translation.error.formError}
                      </label>
                    </Row>
                  )}
                {/* Input lot name */}
                <Row type="flex" align="middle">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label
                        className={
                          props.errors.name && props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.lot.production.drawer.form.name}
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <Input
                        placeholder={
                          translation.lot.production.drawer.form.namePlaceHolder
                        }
                        value={props.values.name}
                        name="name"
                        autoComplete="chrome-off"
                        ref={inputNameRef}
                        onChange={(e) => {
                          props.setFieldValue("name", e.target.value);
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {/* Input lot identification */}
                <Row type="flex" align="middle">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label>
                        {translation.lot.production.drawer.form.identification}
                        <InfoTooltip title="A identificação será utilizada nos processos de importação." />
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <Input
                        placeholder={
                          translation.lot.production.drawer.form
                            .identificationPlaceHolder
                        }
                        value={props.values.identification}
                        name="name"
                        autoComplete="chrome-off"
                        onChange={(e) => {
                          props.setFieldValue(
                            "identification",
                            e.target.value.length > 0 ? e.target.value : null,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {/* Input profitCenterId */}
                <Row type="flex" align="middle">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label
                        className={
                          props.errors.profitCenterId && props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.lot.production.drawer.form.profitCenter}
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <Select
                        name="profitCenterId"
                        value={
                          props.values.profitCenterId != null
                            ? profitCenters != null &&
                              profitCenters.find(
                                (pc) => pc.id === props.values.profitCenterId,
                              ) != null
                              ? props.values.profitCenterId
                              : undefined
                            : undefined
                        }
                        placeholder={
                          translation.lot.production.drawer.form
                            .profitCenterPlaceHolder
                        }
                        onChange={(value) => {
                          props.setFieldValue("profitCenterId", value);
                          if (props.values.id === null) {
                            props.setFieldValue("picketId", null);
                            props.setFieldValue("picketLotEntry", null);
                            setSelectedPicket(null);
                          }
                        }}
                      >
                        {profitCenters != null &&
                        Object.entries(profitCenters).length > 0
                          ? profitCenters.map((pc) =>
                              pc.productionSubSystem !== null &&
                              pc.productionSubSystem !== "" &&
                              pc.productionSystemId !== null ? (
                                <Option key={pc.id} value={pc.id}>
                                  {pc.name}
                                </Option>
                              ) : null,
                            )
                          : null}
                      </Select>
                    </Row>
                  </Col>
                </Row>
                {/* Input  Carcass Harnessing  */}
                <Row type="flex" justify="start">
                  <Col span={12}>
                    <Row>
                      <label>
                        {
                          translation.lot.production.drawer.form
                            .carcassHarnessing
                        }
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        customInput={Input}
                        value={
                          props.values?.carcassHarnessing !== null
                            ? props.values?.carcassHarnessing
                            : null
                        }
                        placeholder={
                          translation.lot.production.drawer.form
                            .informPlaceHolder
                        }
                        name="carcassHarnessing"
                        addonAfter="%"
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue("carcassHarnessing", floatValue);
                        }}
                      />
                    </Row>
                  </Col>

                  <Col span={11} offset={1}>
                    <Row>
                      <label>
                        {
                          translation.lot.production.drawer.form
                            .asymptoticWeight
                        }
                      </label>
                      <InfoTooltip
                        title="Peso assintótico é o peso que, com base nas informações que o usuário/produtor possui, sobre o histórico do Animal ou Lote de Animais, (considerando a genética, manejo anterior, interação genótipo-ambiente, frame, entre outras variáveis), acredite ser o potencial limite de peso. Ou seja, acima deste peso, dificilmente este animal ganhará peso, em condições normais."
                        placement="bottom"
                      />
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        customInput={Input}
                        value={
                          props.values?.asymptoticWeight !== null
                            ? props.values?.asymptoticWeight
                            : null
                        }
                        placeholder={
                          translation.lot.production.drawer.form
                            .informPlaceHolder
                        }
                        name="asymptoticWeight"
                        addonAfter="kg"
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        disabled={lotIdEdit == null || amountAnimals === 0}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue("asymptoticWeight", floatValue);
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                {amountAnimals === 0 ? (
                  <InfoCard>
                    <NoteIcon /> Adicione animais ao lote para determinar o Peso
                    assintótico
                  </InfoCard>
                ) : null}
                {/************** Input dados de referência *************************/}
                <CustomDivider dashed>
                  DADOS REFERENTE À AQUISIÇÃO DO LOTE
                </CustomDivider>
                {props.values?.id !== null &&
                  props.values.dietStrategyBaseLine == null && (
                    <Row type="flex" justify="end">
                      <ButtonStandard
                        type="button"
                        buttonType="alternative"
                        onClick={() => setShowCardUpdateAcquisitionData(true)}
                      >
                        Atualizar dados
                      </ButtonStandard>
                    </Row>
                  )}
                {showCardUpdateAcquisitionData && (
                  <InfoCard style={{ marginBottom: 16 }}>
                    <Row type="flex" justify="start" gutter={16}>
                      <Col span={24}>
                        <span>
                          Ao atualizar os dados do Lote conforme os animais.
                        </span>
                      </Col>
                    </Row>
                    <Row
                      type="flex"
                      justify="end"
                      gutter={8}
                      style={{ marginTop: 12 }}
                    >
                      <Col>
                        <ButtonStandard
                          type="button"
                          buttonType="type7"
                          onClick={() =>
                            setShowCardUpdateAcquisitionData(false)
                          }
                        >
                          Cancelar
                        </ButtonStandard>
                      </Col>
                      <Col>
                        <ButtonStandard
                          type="button"
                          buttonType="type6"
                          onClick={() => {
                            setShowCardUpdateAcquisitionData(false);
                            handleUpdateLotAcquisitionData();
                          }}
                        >
                          Confirmar
                        </ButtonStandard>
                      </Col>
                    </Row>
                  </InfoCard>
                )}
                {/* Input Raça, Gender and Cap */}
                <Row type="flex" justify="start" gutter={4}>
                  {/* Breed */}
                  <Col span={props.values?.gender === "Male" ? 12 : 16}>
                    <Row>
                      <label>Raça</label>
                    </Row>
                    <Row className="rowInput">
                      <Select
                        style={{ width: "100%" }}
                        name="breedId"
                        placeholder={
                          translation.animal.edit.form.breedPlaceHolder
                        }
                        value={
                          breeds != null && props.values?.breedId != null
                            ? props.values?.breedId
                            : undefined
                        }
                        disabled={props.values.dietStrategyBaseLine != null}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.props.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        onChange={(value) => {
                          const newValues = {
                            ...props.values,
                            breedId: value,
                          };
                          props.setValues(newValues);
                        }}
                      >
                        {breeds != null &&
                          Object.entries(breeds).length > 0 &&
                          breeds.map((breed) => (
                            <Option key={breed.id} value={breed.id}>
                              {breed.name}
                            </Option>
                          ))}
                      </Select>
                    </Row>
                  </Col>
                  {/* Gender */}
                  <Col span={props.values?.gender === "Male" ? 6 : 8}>
                    <Row>
                      <label>{translation.animal.edit.form.sex}</label>
                    </Row>
                    <Row className="rowInput">
                      <Select
                        style={{ width: "100%" }}
                        name="gender"
                        value={
                          props.values?.gender != null
                            ? props.values?.gender
                            : undefined
                        }
                        disabled={props.values.dietStrategyBaseLine != null}
                        placeholder={translation.defaultSelectPlaceholder}
                        onChange={(value) => {
                          const newValues = {
                            ...props.values,
                            gender: value,
                            cap: value === "Female" ? false : props.values.cap,
                          };
                          props.setValues(newValues);
                        }}
                      >
                        <Option key="Female" value="Female">
                          {translation.animal.edit.form.female}
                        </Option>
                        <Option key="Male" value="Male">
                          {translation.animal.edit.form.male}
                        </Option>
                      </Select>
                    </Row>
                  </Col>
                  {/* Cap */}
                  {props.values?.gender === "Male" ? (
                    <Col span={6}>
                      <Row>
                        <label className="formLabel">
                          {translation.animal.edit.form.cap}
                        </label>
                      </Row>
                      <Row className="rowInput">
                        <RadioGroup
                          name="cap"
                          value={props.values?.cap}
                          disabled={props.values.dietStrategyBaseLine != null}
                          defaultValue={false}
                          onChange={(e) => {
                            const newValues = {
                              ...props.values,
                              cap: e.target.value,
                            };
                            props.setValues(newValues);
                          }}
                        >
                          <RadioButton value={true}>
                            {translation.buttons.yes}
                          </RadioButton>
                          <RadioButton value={false}>
                            {translation.buttons.no}
                          </RadioButton>
                        </RadioGroup>
                      </Row>
                    </Col>
                  ) : null}
                </Row>
                {/* Input data de aquisição, Nascimento animal e Peso médio */}
                <Row type="flex" justify="start" gutter={4}>
                  {/* Input data aquisição animal */}
                  <Col span={8}>
                    <Row>
                      <label>Data de aquisição</label>
                    </Row>
                    <Row className="rowInput">
                      <DatePicker
                        name="referenceAcquisitionDate"
                        allowClear={false}
                        value={
                          props.values.referenceAcquisitionDate != null
                            ? moment(
                                props.values.referenceAcquisitionDate,
                                "YYYY-MM-DD",
                              )
                            : null
                        }
                        disabled={props.values.dietStrategyBaseLine != null}
                        placeholder={translation.defaultDatePickerPlaceholder}
                        format={dateFormat}
                        onChange={(date, dateString) => {
                          if (date <= moment() && date !== null) {
                            props.setValues({
                              ...props.values,
                              referenceAcquisitionDate: date,
                            });
                          }
                        }}
                      />
                    </Row>
                  </Col>
                  {/* Input nascimento animal */}
                  <Col span={8}>
                    <Row>
                      <label>Nascimento animal</label>
                    </Row>
                    <Row className="rowInput">
                      <DatePicker
                        name="referenceBirthdayDate"
                        allowClear={false}
                        value={
                          props.values.referenceBirthdayDate != null
                            ? moment(
                                props.values.referenceBirthdayDate,
                                "YYYY-MM-DD",
                              )
                            : null
                        }
                        disabled={props.values.dietStrategyBaseLine != null}
                        placeholder={translation.defaultDatePickerPlaceholder}
                        format={dateFormat}
                        onChange={(date, dateString) => {
                          if (date <= moment() && date !== null) {
                            props.setValues({
                              ...props.values,
                              referenceBirthdayDate: date,
                            });
                          }
                        }}
                      />
                    </Row>
                  </Col>
                  {/* Input peso médio por animal */}
                  <Col span={8}>
                    <Row>
                      <label>Peso médio por animal</label>
                    </Row>
                    <Row className="rowInput">
                      <NumberFormat
                        customInput={Input}
                        value={
                          props.values?.referenceAcquisitionWeight !== null
                            ? props.values?.referenceAcquisitionWeight
                            : null
                        }
                        placeholder={
                          translation.lot.production.drawer.form
                            .informPlaceHolder
                        }
                        disabled={props.values.dietStrategyBaseLine != null}
                        name="referenceAcquisitionWeight"
                        addonAfter="Kg"
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          props.setFieldValue(
                            "referenceAcquisitionWeight",
                            floatValue,
                          );
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                <CustomDivider dashed />
                {/* Input mudar finalidade dos animais */}
                {props.values.id !== null && (
                  <Row type="flex" justify="start">
                    <Col span={16}>
                      <Row>
                        <label
                          className={
                            props.errors.averageAnimalStay &&
                            props.submitCount > 0
                              ? "error"
                              : ""
                          }
                        >
                          Deseja mudar a "Finalidade" dos animais do Lote?
                        </label>
                      </Row>
                      <Row className="rowInput">
                        <RadioGroup
                          name="changeAnimalsFunction"
                          defaultValue={false}
                          value={
                            props.values.changeAnimalsFunction === true
                              ? true
                              : false
                          }
                          onChange={(e) =>
                            props.setFieldValue(
                              "changeAnimalsFunction",
                              e.target.value,
                            )
                          }
                        >
                          <RadioButton value={true}>
                            {translation.buttons.yes}
                          </RadioButton>
                          <RadioButton value={false}>
                            {translation.buttons.no}
                          </RadioButton>
                        </RadioGroup>
                      </Row>
                    </Col>
                    {props.values.changeAnimalsFunction === true && (
                      <Col span={8}>
                        <Row type="flex" justify="start">
                          <Col span={24}>
                            <Row>
                              <label
                                className={
                                  props.errors.animalFarmFunction &&
                                  props.submitCount > 0
                                    ? "formLabel error"
                                    : "formLabel"
                                }
                              >
                                Nova Finalidade*
                              </label>
                            </Row>
                            <Row className="rowInput">
                              <Select
                                name="animalFarmFunction"
                                value={
                                  props.values.animalFarmFunction || undefined
                                }
                                placeholder={
                                  translation.defaultSelectPlaceholder
                                }
                                onChange={(value) => {
                                  props.setFieldValue(
                                    "animalFarmFunction",
                                    value,
                                  );
                                }}
                              >
                                <Option value="reproduction">Reprodução</Option>
                                <Option value="fatten">Engorda</Option>
                              </Select>
                            </Row>
                          </Col>
                        </Row>
                        {props.values.animalFarmFunction === "reproduction" && (
                          <Row type="flex" justify="start">
                            <Col span={24}>
                              <Row>
                                <label
                                  className={
                                    props.errors.animalFarmFunction &&
                                    props.submitCount > 0
                                      ? "formLabel error"
                                      : "formLabel"
                                  }
                                >
                                  Nova Categ. de Reprodução para fêmeas*
                                </label>
                              </Row>
                              <Row className="rowInput">
                                <Select
                                  name="animalReproductionCategory"
                                  value={
                                    props.values.animalReproductionCategory ||
                                    undefined
                                  }
                                  placeholder={
                                    translation.defaultSelectPlaceholder
                                  }
                                  onChange={(value) =>
                                    props.setFieldValue(
                                      "animalReproductionCategory",
                                      value,
                                    )
                                  }
                                >
                                  <Option key="nulipara" value="nulipara">
                                    Nulípara
                                  </Option>
                                  <Option key="novilha" value="novilha">
                                    Novilha
                                  </Option>
                                  <Option key="primipara" value="primipara">
                                    Primípara
                                  </Option>
                                  <Option key="secundipara" value="secundipara">
                                    Secundípara
                                  </Option>
                                  <Option key="multipara" value="multipara">
                                    Multípara
                                  </Option>
                                </Select>
                              </Row>
                            </Col>
                          </Row>
                        )}
                      </Col>
                    )}
                  </Row>
                )}
                {/* Input média de permanência dos animais na fazenda */}
                <Row type="flex" justify="start">
                  <Col span={20}>
                    <Row>
                      <label
                        className={
                          props.errors.averageAnimalStay &&
                          props.submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        Média de permanência dos animais na fazenda
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <InputGroup compact>
                        <Input
                          type="number"
                          style={{ width: 74, textAlign: "center" }}
                          name="ageDay"
                          min="0"
                          placeholder="Dias"
                          value={
                            props.values.averageAnimalStay != null
                              ? props.values.averageAnimalStay
                              : null
                          }
                          onChange={(e) => handleChangeAge(e, "days", props)}
                        />
                        <Input
                          style={{
                            width: 74,
                            textAlign: "center",
                            borderLeft: "1px solid #c4c4c4",
                          }}
                          type="number"
                          name="ageMonth"
                          min="0"
                          placeholder="Meses"
                          value={
                            props.values.averageAnimalStay != null
                              ? Math.round(
                                  moment
                                    .duration(
                                      props.values.averageAnimalStay,
                                      "days",
                                    )
                                    .asMonths(),
                                )
                              : null
                          }
                          onChange={(e) => handleChangeAge(e, "months", props)}
                        />
                        <Input
                          style={{
                            width: 74,
                            textAlign: "center",
                            borderLeft: "1px solid #c4c4c4",
                          }}
                          type="number"
                          name="ageYear"
                          min="0"
                          placeholder="Anos"
                          value={
                            props.values.averageAnimalStay != null
                              ? Math.round(
                                  moment
                                    .duration(
                                      props.values.averageAnimalStay,
                                      "days",
                                    )
                                    .asYears(),
                                )
                              : null
                          }
                          onChange={(e) => handleChangeAge(e, "years", props)}
                        />
                      </InputGroup>
                    </Row>
                  </Col>
                </Row>
                {/* Input projectionWeightType + dietStrategyBaseLine / weightGainProjection */}
                <CustomDivider dashed />
                <Row type="flex">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Row>
                      <label>
                        {
                          translation.lot.production.drawer.form
                            .projectionWeightType
                        }
                      </label>
                    </Row>
                    <Row className="rowInput">
                      <Radio.Group
                        value={props.values.projectionWeightType}
                        onChange={(e) => {
                          props.setFieldValue(
                            "projectionWeightType",
                            e.target.value,
                          );
                          props.setFieldValue("dietStrategyBaseLine", null);
                          props.setFieldValue("dietStrategies", null);
                          props.setFieldValue("weightGainProjectionId", null);
                          props.setFieldValue(
                            "startWeightGainProjectionDate",
                            null,
                          );
                        }}
                        style={{ marginTop: 6 }}
                      >
                        <Radio value="DIET_STRATEGY">
                          {
                            translation.lot.production.drawer.form
                              .projectionWeightTypeDietStrategy
                          }
                        </Radio>
                        <Radio value="FARM_PROJECTION_GAIN_WEIGHT">
                          {
                            translation.lot.production.drawer.form
                              .projectionWeightTypeFarmProjection
                          }
                        </Radio>
                      </Radio.Group>
                    </Row>
                  </Col>
                </Row>
                {props.values.projectionWeightType === "DIET_STRATEGY" && (
                  <Row type="flex" style={{ marginTop: 8 }}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                      <Row>
                        <label>
                          {
                            translation.lot.production.drawer.form
                              .dietStrategyBaseLine
                          }
                        </label>
                        <p>
                          {
                            translation.lot.production.drawer.form
                              .dietStrategyBaseLineMessage
                          }
                        </p>
                      </Row>
                      <Row className="rowInput">
                        <Select
                          name="dietStrategyBaseLine"
                          loading={isLoadingDietStrategies}
                          value={
                            dietStrategies.length > 0
                              ? props.values.dietStrategyBaseLine != null
                                ? props.values.dietStrategyBaseLine
                                    .dietStrategyId
                                : undefined
                              : undefined
                          }
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.props.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          placeholder="Selecione a estratégia"
                          allowClear
                          onChange={(value) =>
                            handleSelectDietStrategyBaseLine(value, props)
                          }
                          style={{ marginTop: 6 }}
                        >
                          {dietStrategies.map((ds) => (
                            <Option key={ds.id} value={ds.id}>
                              {ds.name}
                            </Option>
                          ))}
                        </Select>
                      </Row>
                    </Col>
                  </Row>
                )}
                {props.values.projectionWeightType ===
                  "FARM_PROJECTION_GAIN_WEIGHT" && (
                  <Row type="flex" style={{ marginTop: 8 }}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                      <Row>
                        <label
                          className={
                            props.errors.startWeightGainProjectionDate &&
                            props.submitCount > 0
                              ? "error"
                              : ""
                          }
                        >
                          {
                            translation.lot.production.drawer.form
                              .startWeightGainProjectionDate
                          }
                          *
                        </label>
                      </Row>
                      <Row className="rowInput">
                        <DatePicker
                          name="startWeightGainProjectionDate"
                          allowClear
                          value={
                            props.values.startWeightGainProjectionDate != null
                              ? moment(
                                  props.values.startWeightGainProjectionDate,
                                  "YYYY-MM-DD",
                                )
                              : null
                          }
                          placeholder={translation.defaultDatePickerPlaceholder}
                          format={dateFormat}
                          disabledDate={(current) =>
                            current && current.isAfter(moment(), "day")
                          }
                          onChange={(date) => {
                            props.setFieldValue(
                              "startWeightGainProjectionDate",
                              date,
                            );
                          }}
                        />
                      </Row>
                    </Col>
                  </Row>
                )}
                {props.values.projectionWeightType ===
                  "FARM_PROJECTION_GAIN_WEIGHT" && (
                  <Row type="flex" style={{ marginTop: 8 }}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                      <Row>
                        <label>
                          {
                            translation.lot.production.drawer.form
                              .weightGainProjection
                          }
                        </label>
                      </Row>
                      <Row className="rowInput">
                        <Select
                          name="weightGainProjectionId"
                          loading={isLoadingWeightGainProjections}
                          value={
                            props.values.weightGainProjectionId || undefined
                          }
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.props.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          placeholder={
                            translation.lot.production.drawer.form
                              .weightGainProjectionPlaceholder
                          }
                          allowClear
                          onChange={(value) =>
                            props.setFieldValue(
                              "weightGainProjectionId",
                              value || null,
                            )
                          }
                          style={{ marginTop: 6 }}
                        >
                          {weightGainProjections.map((wgp) => (
                            <Option key={wgp.id} value={wgp.id}>
                              {wgp.name}
                            </Option>
                          ))}
                        </Select>
                      </Row>
                    </Col>
                  </Row>
                )}
                {!lotAlreadyHavePicket ? (
                  <>
                    <CustomDivider dashed>ALOCAR LOTE EM PIQUETE</CustomDivider>
                    <Row type="flex" gutter={16}>
                      <Col span={12}>
                        <Row>
                          <label>
                            {translation.lot.production.drawer.form.picketId}
                          </label>
                        </Row>
                        <Row className="rowInput">
                          <Select
                            name="picketId"
                            value={props.values.picketId || undefined}
                            placeholder="Selecione o piquete"
                            allowClear
                            onChange={(value) => {
                              props.setFieldValue("picketId", value);
                              if (value == null) {
                                props.setFieldValue("picketLotEntry", null);
                                setSelectedPicket(null);
                              } else {
                                setSelectedPicket(
                                  pickets.find((p) => p.id === value),
                                );
                              }
                            }}
                          >
                            {pickets
                              .filter((picket) => picket.status === "Break")
                              .filter(
                                (picket) =>
                                  picket.profitCenterId ===
                                  props.values.profitCenterId,
                              )
                              .map((picket) => (
                                <Option key={picket.id} value={picket.id}>
                                  {picket.name}
                                </Option>
                              ))}
                          </Select>
                        </Row>
                      </Col>
                      <Col span={8}>
                        <Row>
                          <label
                            className={
                              props.errors.picketLotEntry &&
                              props.submitCount > 0
                                ? "error"
                                : ""
                            }
                          >
                            {
                              translation.lot.production.drawer.form
                                .picketLotEntry
                            }
                            {selectedPicket != null && "*"}
                          </label>
                        </Row>
                        <Row className="rowInput">
                          <DatePicker
                            name="picketLotEntry"
                            allowClear
                            value={
                              props.values.picketLotEntry != null
                                ? moment(
                                    props.values.picketLotEntry,
                                    "YYYY-MM-DD",
                                  )
                                : undefined
                            }
                            placeholder={
                              translation.defaultDatePickerPlaceholder
                            }
                            format={dateFormat}
                            disabledDate={(current) =>
                              selectedPicket != null &&
                              selectedPicket.lastLotMovimentDate != null
                                ? current.isBefore(
                                    moment(selectedPicket.lastLotMovimentDate),
                                  ) || current.isAfter(moment())
                                : current.isAfter(moment())
                            }
                            disabled={!selectedPicket}
                            onChange={(date) => {
                              props.setFieldValue("picketLotEntry", date);
                            }}
                          />
                        </Row>
                      </Col>
                    </Row>
                  </>
                ) : (
                  <>
                    <CustomDivider dashed>
                      DADOS REFERENTE AO PIQUETE
                    </CustomDivider>
                    <Row type="flex" gutter={16}>
                      <Col span={12}>
                        <Row>
                          <label>
                            {translation.lot.production.drawer.form.picketId}
                          </label>
                        </Row>
                        <Row className="rowInput">
                          <strong>{props.values.picketName}</strong>
                        </Row>
                      </Col>
                      {props.values.picketLotEntry != null ? (
                        <Col span={8}>
                          <Row>
                            <label>
                              {
                                translation.lot.production.drawer.form
                                  .picketLotEntry
                              }
                            </label>
                          </Row>
                          <Row className="rowInput">
                            <strong>
                              {moment(props.values.picketLotEntry).format(
                                "DD/MM/YYYY",
                              )}
                            </strong>
                          </Row>
                        </Col>
                      ) : null}
                    </Row>
                  </>
                )}
              </Spin>
            </div>
            {/* Footer */}
            <Footer>
              <Row type="flex" justify="space-between">
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  className="selectDiv"
                >
                  {props.values.id === null ? (
                    <Select
                      value={saveOptions}
                      name="saveOptions"
                      onChange={(value) => setSaveOptions(value)}
                    >
                      <Option key="1" value="only_save">
                        {translation.lot.production.saveOptions.only_save}
                      </Option>
                      <Option key="2 " value="save_and_create_new">
                        {
                          translation.lot.production.saveOptions
                            .save_and_create_new
                        }
                      </Option>
                    </Select>
                  ) : null}
                </Col>
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  className="buttonsDiv"
                >
                  <ButtonStandard
                    type="button"
                    buttonType="type7"
                    onClick={closeDrawer}
                  >
                    {translation.buttons.cancel}
                  </ButtonStandard>

                  <ButtonStandard type="submit" buttonType="type6">
                    {translation.buttons.save}
                  </ButtonStandard>
                </Col>
              </Row>
            </Footer>
          </form>
        )}
      />
    </Container>
  );
};

export default DrawerLotProduction;
